import { useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Info, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { isLikelyAsin, parseAsinList } from '@/utils/spCampaignCreateDraft';

const PRODUCT_EXCLUSION_HELP =
  'https://advertising.amazon.com/library/guides/sponsored-brands-what-to-know';

function mergeAsins(existing: string[], incoming: string[]): string[] {
  const seen = new Set(existing);
  const next = [...existing];
  for (const asin of incoming) {
    if (seen.has(asin)) continue;
    seen.add(asin);
    next.push(asin);
  }
  return next;
}

export function SbProductExclusionsSection({
  asins,
  onChange,
  disabled,
}: {
  asins: string[];
  onChange: (asins: string[]) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'list' | 'upload'>('list');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const excludeText = (raw: string) => {
    const tokens = parseAsinList(raw);
    const valid = tokens.filter((asin) => isLikelyAsin(asin));
    if (tokens.length > 0 && valid.length === 0) {
      setError('Enter ASINs such as B000000001.');
      return;
    }
    if (valid.length === 0) return;
    onChange(mergeAsins(asins, valid));
    setText('');
    setError(tokens.length === valid.length ? null : 'Some entries were not ASINs and were skipped.');
  };

  return (
    <section className="rounded-xl border border-border bg-surface p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-2 text-left"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? (
              <ChevronDown className="h-4 w-4 shrink-0 text-foreground-muted" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 text-foreground-muted" />
            )}
            <span className="text-base font-medium text-foreground">Product exclusions</span>
          </button>
          <span title="Products you exclude are left out of this automatic collection." className="inline-flex text-foreground-subtle">
            <Info className="h-3.5 w-3.5" aria-hidden />
            <span className="sr-only">Products you exclude are left out of this automatic collection.</span>
          </span>
          <span className="text-sm text-foreground-subtle">optional</span>
        </div>
        <a
          href={PRODUCT_EXCLUSION_HELP}
          target="_blank"
          rel="noreferrer"
          className="inline-flex shrink-0 items-center gap-1 text-sm text-brand hover:underline"
        >
          <Info className="h-3.5 w-3.5" aria-hidden />
          How product exclusion works
        </a>
      </div>

      {open ? (
        <div className="mt-4 grid grid-cols-1 overflow-hidden rounded-lg border border-border lg:grid-cols-2">
          <div className="bg-surface p-4">
            <div className="flex gap-4 border-b border-border text-sm">
              <button
                type="button"
                className={cn(
                  '-mb-px border-b-2 px-1 py-2 font-medium',
                  tab === 'list' ? 'border-brand text-foreground' : 'border-transparent text-foreground-muted'
                )}
                onClick={() => setTab('list')}
              >
                Enter list
              </button>
              <button
                type="button"
                className={cn(
                  '-mb-px border-b-2 px-1 py-2 font-medium',
                  tab === 'upload' ? 'border-brand text-foreground' : 'border-transparent text-foreground-muted'
                )}
                onClick={() => setTab('upload')}
              >
                Upload
              </button>
            </div>
            {tab === 'list' ? (
              <div className="pt-4">
                <textarea
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  placeholder="Enter ASINs separated by a comma, space or new line."
                  className="min-h-[140px] w-full rounded-md border border-border px-3 py-2 text-sm"
                  disabled={disabled}
                  aria-label="ASINs to exclude"
                />
                {error ? <p className="mt-2 text-xs text-error">{error}</p> : null}
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-40"
                    disabled={disabled || !text.trim()}
                    onClick={() => excludeText(text)}
                  >
                    Exclude
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3 pt-4">
                <p className="text-sm text-foreground-muted">
                  Upload a text or CSV file with one ASIN per line.
                </p>
                <button
                  type="button"
                  className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-40"
                  disabled={disabled}
                  onClick={() => fileRef.current?.click()}
                >
                  Choose file
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".txt,.csv,text/plain,text/csv"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (!file) return;
                    void file.text().then((raw) => excludeText(raw));
                  }}
                />
                {error ? <p className="text-xs text-error">{error}</p> : null}
              </div>
            )}
          </div>
          <div className="border-t border-border bg-page/60 lg:border-l lg:border-t-0">
            <div className="flex items-center justify-between px-4 py-3">
              <p className="text-sm font-semibold">{asins.length} excluded</p>
              <button
                type="button"
                className="text-sm text-foreground-muted disabled:opacity-40"
                disabled={disabled || asins.length === 0}
                onClick={() => onChange([])}
              >
                Remove all
              </button>
            </div>
            <div className="border-t border-border px-4 py-2 text-sm text-foreground-muted">Products</div>
            <ul className="max-h-64 overflow-auto">
              {asins.map((asin) => (
                <li key={asin} className="flex items-center gap-2 border-t border-border px-4 py-2 text-sm">
                  <span className="flex-1 truncate">{asin}</span>
                  <button
                    type="button"
                    disabled={disabled}
                    aria-label={`Remove ${asin}`}
                    onClick={() => onChange(asins.filter((item) => item !== asin))}
                  >
                    <X className="h-4 w-4 text-foreground-muted" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </section>
  );
}
